import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { api } from '@/lib/api';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { FolderOpen, Image, CheckCircle, TrendingUp } from 'lucide-react';

type Stats = {
  totalProjects: number;
  totalImages: number;
  completedImages: number;
  remainingQuota: number;
};

export default function Dashboard() {
  const { user } = useAuthStore();
  const [stats, setStats] = useState<Stats>({
    totalProjects: 0,
    totalImages: 0,
    completedImages: 0,
    remainingQuota: user?.quota || 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadStats() {
      try {
        const projects = await api.projects.list();
        const projectList = projects.data || [];

        let totalImages = 0;
        let completedImages = 0;

        for (const project of projectList) {
          totalImages += project.imageCount || 0;
          completedImages += project.completedCount || 0;
        }

        setStats({
          totalProjects: projectList.length,
          totalImages,
          completedImages,
          remainingQuota: user?.quota || 0,
        });
      } catch (error) {
        console.error('Failed to load stats:', error);
      } finally {
        setLoading(false);
      }
    }

    loadStats();
  }, [user]);

  const statCards = [
    {
      title: '项目总数',
      value: stats.totalProjects,
      icon: FolderOpen,
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: '图片总数',
      value: stats.totalImages,
      icon: Image,
      color: 'text-green-600',
      bgColor: 'bg-green-50',
    },
    {
      title: '已标注',
      value: stats.completedImages,
      icon: CheckCircle,
      color: 'text-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: '剩余配额',
      value: stats.remainingQuota,
      icon: TrendingUp,
      color: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">欢迎回来，{user?.name || user?.email} 👋</h1>
        <p className="text-gray-500 mt-1">这是您的产品标注概览</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title}>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">{stat.title}</p>
                    <p className="text-3xl font-bold mt-1">
                      {loading ? '-' : stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                    <Icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>快速开始</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Link to="/projects">
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <FolderOpen className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">创建新项目</h3>
                      <p className="text-sm text-gray-500">上传产品图片并开始 AI 标注</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>

            <Link to="/projects">
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardContent className="pt-6">
                  <div className="flex items-center space-x-4">
                    <div className="p-3 rounded-lg bg-green-100">
                      <Image className="h-6 w-6 text-green-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold">查看项目</h3>
                      <p className="text-sm text-gray-500">管理您的标注项目和结果</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
